import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PassageAnswer } from '@examing/types';
import { RenderedHtml } from 'modules/editor';
import { PassagePlayer } from 'components/questions/PassagePlayer';
import { cn } from 'lib/utils';
import { ExamAnswerArea, PROMPT_OWNED_TYPES } from './ExamAnswerArea';
import { ListeningSectionView } from './ListeningSectionView';
import { SpeakingSectionView } from './SpeakingSectionView';
import { WritingTask } from './WritingTask';
import { PassageHighlighter } from './PassageHighlighter';
import { useSaveWord } from 'modules/vocabulary';
import { loadHighlights, saveHighlights, type HighlightsByPassage } from './highlightStore';
import { loadSectionPart, saveSectionPart } from './sectionPart';
import type { CandidateQuestion, CandidateSection, SpeakingAnswer } from '../../types';
import { ACTIVE } from './selection';

interface Props {
  attemptId: string;
  section: CandidateSection;
  answers: Map<string, unknown>;
  onAnswer: (questionId: string, value: unknown) => void;
  /** Speaking recordings are stored by their upload; this only mirrors them locally. */
  onRecorded: (questionId: string, value: SpeakingAnswer) => void;
}

/** Stacked questions with IELTS-style numbering (a passage counts each sub-question). */
function StackedQuestions({
  questions,
  answers,
  onAnswer,
}: {
  questions: CandidateQuestion[];
  answers: Map<string, unknown>;
  onAnswer: Props['onAnswer'];
}) {
  const { t } = useTranslation();
  let number = 0;
  return (
    <ol className="mx-auto max-w-3xl space-y-6 p-5 sm:p-8">
      {questions.map((q) => {
        number += 1;
        const label = number;
        return (
          <li key={q.id} id={`exam-q-${q.id}`} className="scroll-mt-24 rounded-lg border border-gray-200 bg-white p-4 sm:p-5">
            <div className="flex gap-3">
              <span className="mt-0.5 flex h-7 min-w-[1.75rem] shrink-0 items-center justify-center rounded-md border border-gray-900 px-1.5 text-sm font-bold text-gray-900">
                {label}
              </span>
              <div className="min-w-0 flex-1 space-y-3">
                {!PROMPT_OWNED_TYPES.has(q.type) && (
                  <div className="text-[15px] leading-relaxed text-gray-900">
                    <RenderedHtml html={q.prompt || ''} />
                  </div>
                )}
                <ExamAnswerArea question={q} value={answers.get(q.id)} onChange={(v) => onAnswer(q.id, v)} />
              </div>
            </div>
          </li>
        );
      })}
      {!questions.length && <p className="text-center text-gray-600">{t('exam.empty_section')}</p>}
    </ol>
  );
}

export function IeltsSectionView({ attemptId, section, answers, onAnswer, onRecorded }: Props) {
  const { t } = useTranslation();
  const saveWord = useSaveWord();
  const passages = useMemo(() => section.questions.filter((q) => q.type === 'PASSAGE'), [section.questions]);
  const others = useMemo(() => section.questions.filter((q) => q.type !== 'PASSAGE'), [section.questions]);
  // The open Reading passage or Writing task; a reload opens the same one.
  const ownsPart = section.kind === 'IELTS_READING' || section.kind === 'IELTS_WRITING';
  const partCount = section.kind === 'IELTS_WRITING' ? section.questions.length : passages.length;
  const [part, setPart] = useState(() => (ownsPart ? loadSectionPart(attemptId, section.id, partCount) : 0));
  useEffect(() => {
    if (ownsPart) saveSectionPart(attemptId, section.id, part);
  }, [ownsPart, attemptId, section.id, part]);
  // Keyed by passage and page. Kept here so highlights survive part switches,
  // and dropped with the section, as in the computer-delivered test. Saved in
  // this browser so a reload keeps them.
  const [highlights, setHighlights] = useState<HighlightsByPassage>(() => loadHighlights(attemptId, section.id));
  useEffect(() => {
    saveHighlights(attemptId, section.id, highlights);
  }, [attemptId, section.id, highlights]);

  if (section.kind === 'IELTS_WRITING') {
    const tasks = section.questions;
    const current = tasks[Math.min(part, tasks.length - 1)];
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        {current ? (
          <WritingTask
            key={current.id}
            question={current}
            value={typeof answers.get(current.id) === 'string' ? (answers.get(current.id) as string) : ''}
            onChange={(v) => onAnswer(current.id, v)}
          />
        ) : (
          <p className="p-8 text-center text-gray-600">{t('exam.empty_section')}</p>
        )}
        {tasks.length > 1 && (
          <PartTabs
            labels={tasks.map((q, i) =>
              q.writingTask === 'TASK_1' ? t('exam.writing.task1') : q.writingTask === 'TASK_2' ? t('exam.writing.task2') : t('exam.part', { n: i + 1 }),
            )}
            active={part}
            onSelect={setPart}
            done={tasks.map((q) => typeof answers.get(q.id) === 'string' && (answers.get(q.id) as string).trim() !== '')}
          />
        )}
      </div>
    );
  }

  if (section.kind === 'IELTS_READING' && passages.length) {
    const current = passages[Math.min(part, passages.length - 1)];
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        {/* Keyed by passage so switching parts opens the new one at its top. */}
        <div key={current.id} className="min-h-0 flex-1 overflow-y-auto px-4 py-3 sm:px-7 sm:py-4">
          <PassagePlayer
            passageText={current.passageText || ''}
            passageTitle={current.passageTitle}
            subQuestions={current.subQuestions ?? []}
            pages={current.pages}
            value={(answers.get(current.id) as PassageAnswer) || {}}
            onChange={(v) => onAnswer(current.id, v)}
            renderPassage={(passage, pageIndex) => {
              const key = `${current.id}:${pageIndex}`;
              return (
                <PassageHighlighter
                  contentKey={key}
                  highlights={highlights[key] ?? []}
                  onChange={(update) => setHighlights((prev) => ({ ...prev, [key]: update(prev[key] ?? []) }))}
                  onSaveWord={saveWord}
                >
                  {passage}
                </PassageHighlighter>
              );
            }}
          />
          {others.length > 0 && part === passages.length - 1 && (
            <StackedQuestions questions={others} answers={answers} onAnswer={onAnswer} />
          )}
        </div>
        {passages.length > 1 && (
          <PartTabs
            labels={passages.map((p, i) => p.passageTitle || t('exam.part', { n: i + 1 }))}
            active={part}
            onSelect={setPart}
            done={passages.map((p) => {
              const v = answers.get(p.id);
              return Boolean(v && typeof v === 'object' && Object.keys(v as object).length >= (p.subQuestions?.length ?? 1));
            })}
          />
        )}
      </div>
    );
  }

  if (section.kind === 'IELTS_SPEAKING') {
    return <SpeakingSectionView attemptId={attemptId} section={section} answers={answers} onRecorded={onRecorded} />;
  }

  if (section.kind === 'IELTS_LISTENING') {
    return <ListeningSectionView attemptId={attemptId} section={section} answers={answers} onAnswer={onAnswer} />;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto bg-[#F6F5F1]">
        <StackedQuestions questions={section.questions} answers={answers} onAnswer={onAnswer} />
      </div>
    </div>
  );
}

function PartTabs({
  labels,
  active,
  onSelect,
  done,
}: {
  labels: string[];
  active: number;
  onSelect: (i: number) => void;
  done: boolean[];
}) {
  const { t } = useTranslation();
  return (
    <nav className="flex gap-1 overflow-x-auto border-t border-gray-200 bg-white px-4 py-2 sm:px-7" aria-label={t('exam.parts')}>
      {labels.map((label, i) => (
        <button
          key={label + i}
          type="button"
          onClick={() => onSelect(i)}
          aria-current={i === active ? 'page' : undefined}
          className={cn(
            'shrink-0 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
            i === active ? ACTIVE : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
          )}
        >
          {label}
          {done[i] && <span className="ml-1.5 text-xs opacity-70">✓</span>}
        </button>
      ))}
    </nav>
  );
}

export default IeltsSectionView;
